/// <amd-module name="Repos/PullRequestList/PullRequestDataProviderService" />
import { GitPullRequest, IdentityRefWithVote, PullRequestStatus } from "Repos/Common/Contracts/Git";
import { IReposRoutingService } from "Repos/Common/ReposRoutingService";
import * as Resources from "Repos/PullRequestList/Resources";
import { IReadyableReadonlyObservableArray, ObservableArray, ObservableLike, ObservableValue, ReadyableObservableArray } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { PersonaDetailsProvider } from "VSS/Features/Identities/PersonaDetailsProvider";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssPageService } from "VSS/Platform/Page";
import { IVssContributedTab } from "VSSUI/Tabs";
import {
    IArtifactStatsDataProviderData,
    IPullRequest,
    IPullRequestArtifactStats,
    IPullRequestQuery,
    IPullRequestReviewer,
    IPullRequestSection,
    PullRequestVote
} from "./Contracts";
import { getGitClient } from "./RestClient/Git";
import { convertPullRequest, convertReviewer } from "./Util";

export interface IPullRequestDataProviderService extends IVssService {
    getPullRequestQueries(ignoreCache?: boolean): Promise<IPullRequestQuery[]>;
    getPullRequestSections(ignoreCache?: boolean): IReadyableReadonlyObservableArray<IPullRequestSection>;
    getMyWorkPullRequestSections(ignoreCache?: boolean): IReadyableReadonlyObservableArray<IPullRequestSection>;
    toggleDeclineToReview(pullRequest: IPullRequest): void;
    toggleFlagged(pullRequest: IPullRequest): void;
    updatePullRequestsWithStats(pullRequests: IPullRequest[]): void;
}

interface IPullRequestArtifact {
    artifactId: string;
    discussionArtifactId: string;
}

interface IInternalPullRequestSection extends IPullRequestSection {
    allPullRequests: IPullRequest[];
}

const prListDataProviderId = "ms.vss-code-web.prs-list-data-provider";

interface IPullRequestQueryResult {
    hasMore?: boolean;
    ids: number[];
}

interface IPullRequestListData {
    partialResults?: boolean;
    pullRequests: { [id: number]: GitPullRequest };
    queries: IPullRequestQuery[];
    queryResults: { [id: string]: IPullRequestQueryResult };
}

class PullRequestDataProviderService extends VssService implements IPullRequestDataProviderService {
    private myWorkSections: ReadyableObservableArray<IPullRequestSection>;
    private personaProvider: PersonaDetailsProvider;
    private queries: Promise<IPullRequestQuery[]>;
    private sections: ReadyableObservableArray<IInternalPullRequestSection>;
    private userId: string;

    public _serviceStart(pageContext: IVssPageContext): void {
        super._serviceStart(pageContext);

        this.personaProvider = new PersonaDetailsProvider(pageContext);
        this.userId = pageContext.getService<IVssPageService>("IVssPageService").getData()!.user.id;
    }

    public getPullRequestQueries(ignoreCache?: boolean): Promise<IPullRequestQuery[]> {
        if (!this.queries || ignoreCache) {
            this.getPullRequestSections(ignoreCache);
        }
        return this.queries;
    }

    public getPullRequestSections(ignoreCache?: boolean): ReadyableObservableArray<IPullRequestSection> {
        if (!this.sections || ignoreCache) {
            if (!this.sections) {
                this.sections = new ReadyableObservableArray<IInternalPullRequestSection>();
            }

            let data: IPullRequestListData | undefined;
            const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
            if (ignoreCache) {
                // Clear existing sections and queries
                this.sections.ready.value = false;
            } else {
                data = contributionService.getData<IPullRequestListData>(prListDataProviderId);
            }

            if (data) {
                this.queries = Promise.resolve(data.queries);
                if (ignoreCache) {
                    this.deleteOldSections(data.queries.map(q => q.id));
                }

                this.dataToSections(this.sections, data, true);
            } else {
                this.queries = new Promise((resolve, reject) => {
                    contributionService.getDataAsync<IPullRequestListData>(prListDataProviderId, undefined, ignoreCache).then(data => {
                        resolve(data!.queries);

                        if (ignoreCache) {
                            this.deleteOldSections(data!.queries.map(q => q.id));
                        }

                        this.dataToSections(this.sections, data!, true);
                    }, reject);
                });
            }
        }
        return this.sections;
    }

    public deleteOldSections(newQueryIds: string[]) {
        this.sections.removeAll(section => !newQueryIds.find(id => id === section.query?.id));
    }

    public getMyWorkPullRequestSections(ignoreCache?: boolean): ReadyableObservableArray<IPullRequestSection> {
        if (!this.myWorkSections || ignoreCache) {
            if (!this.myWorkSections) {
                this.myWorkSections = new ReadyableObservableArray<IPullRequestSection>();
            }

            let data: IPullRequestListData | undefined;
            const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
            if (ignoreCache) {
                // Clear existing sections and queries
                this.myWorkSections.ready.value = false;
                this.myWorkSections.value = [];
            } else {
                data = contributionService.getData<IPullRequestListData>(prListDataProviderId, { myWorkQuery: true });
            }

            if (data) {
                this.dataToSections(this.myWorkSections, data, false);
            } else {
                contributionService.getDataAsync<IPullRequestListData>(prListDataProviderId, { myWorkQuery: true }, ignoreCache).then(data => {
                    this.dataToSections(this.myWorkSections, data!, false);
                });
            }
        }
        return this.myWorkSections;
    }

    public toggleDeclineToReview(pullRequest: IPullRequest): void {
        const currentUserId = this.pageContext.getService<IVssPageService>("IVssPageService").getData()!.user.id;
        const { currentReviewer } = pullRequest;

        this.updateReviewer(pullRequest, {
            id: currentUserId,
            hasDeclined: currentReviewer ? !currentReviewer.hasDeclined : true
        } as IdentityRefWithVote);
    }

    public toggleFlagged(pullRequest: IPullRequest): void {
        const currentUserId = this.pageContext.getService<IVssPageService>("IVssPageService").getData()!.user.id;
        const { currentReviewer } = pullRequest;

        this.updateReviewer(pullRequest, {
            id: currentUserId,
            isFlagged: currentReviewer ? !currentReviewer.isFlagged : true
        } as IdentityRefWithVote);
    }

    public updatePullRequestsWithStats(pullRequests: IPullRequest[]): void {
        const pullRequestMap: { [artifactUri: string]: IPullRequest[] } = {};
        const artifacts: IPullRequestArtifact[] = [];
        for (const pr of pullRequests) {
            const artifactId = pr.pullRequestArtifactUri;
            if (!pullRequestMap[artifactId]) {
                pullRequestMap[artifactId] = [];
                artifacts.push({ artifactId: artifactId, discussionArtifactId: pr.codeReviewArtifactUri });
            }
            pullRequestMap[artifactId].push(pr);
        }
        const dataProviderKey = "ms.vss-code-web.pullrequests-artifact-stats-data-provider";
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        contributionService.getDataAsync<IArtifactStatsDataProviderData>(dataProviderKey, { artifactIds: artifacts }, true).then(data => {
            if (data) {
                const statsArray = data["TFS.VersionControl.PullRequestListArtifactStatsProvider.artifactStats"];
                if (statsArray) {
                    for (const stats of statsArray) {
                        const prs = pullRequestMap[stats.pullRequestArtifactId];
                        for (const pr of prs) {
                            pr.commentsCount.value = stats.commentsCount;
                            if (stats.newThreadsCount === null) {
                                pr.unvisited.value = true;
                            } else if (stats.newThreadsCount[0] !== 0) {
                                pr.updates.value = this.getPullRequestStatUpdates(stats);
                            }
                            if (stats.lastUpdatedDate) {
                                pr.date.value = stats.lastUpdatedDate;
                            }
                        }
                    }
                }
            }
        });
    }

    private dataToSections(
        sections: ReadyableObservableArray<IPullRequestSection>,
        data: IPullRequestListData,
        supportsCustomQueries: boolean
    ): void {
        const newSections: IPullRequestSection[] = [];
        const newPullRequests: IPullRequest[] = [];
        for (const query of data.queries) {
            const results = data.queryResults[query.id];
            if (results && results.ids.length) {
                const resultPullRequests = results.ids.map(id => data.pullRequests[id]);
                let tabs: ObservableArray<IVssContributedTab> | undefined;
                let selectedTabId: ObservableValue<string> | undefined;

                const section: IInternalPullRequestSection = {
                    allPullRequests: [],
                    countText: new ObservableValue(""),
                    customizable: supportsCustomQueries && !query.readonly,
                    hasMoreResults: new ObservableValue(!!results.hasMore),
                    query,
                    pullRequests: new ObservableArray<IPullRequest>(),
                    showFlagAndDeclineMenuItems: true,
                    title: query.title
                };

                if (supportsCustomQueries && query.status === PullRequestStatus.Active) {
                    selectedTabId = new ObservableValue("active");
                    tabs = new ObservableArray<IVssContributedTab>();

                    section.tabInfo = {
                        selectedTabId,
                        setSelectedTab: (tabId: string) => {
                            selectedTabId!.value = tabId;
                            this.filterResultsToTab(section, tabId);
                        },
                        tabs
                    };
                }
                if (supportsCustomQueries) {
                    section.loadMore = () => {
                        this.loadMoreQueryResults(section, tabs, selectedTabId);
                    };
                    section.loadMoreLinkProps = {
                        onClick: section.loadMore
                    };
                }
                newSections.push(section);
                newPullRequests.push(...this.addPullRequestsToSection(section, resultPullRequests, results.hasMore, tabs, selectedTabId));
            } else if (results) {
                // Query was returned but it has no data, so let's remove it now.
                // We've kept it till now just to make transition smoother and preserve keyboard focus.
                sections.removeAll(s => s.query?.id === query.id);
            }
        }

        // Replace sections one by one, so we cause minimal churn in UI and keyboard focus
        // Also because we can receive partial sections, and we have to merge with existing ones
        const getId = (section: IPullRequestSection) => section.query?.id || section.title;
        for (const newSection of newSections) {
            const index = sections.value.findIndex(s => getId(s) === getId(newSection)); // check if new section already in sections
            if (index >= 0) {
                // if new section already in sections then replace it with new
                sections.splice(index, 1, newSection);
            } else {
                // otherwise add it to end
                sections.push(newSection);
            }
        }

        // sort only if length of queries is equal to sections, as we can receive partial data, and we can only sort with full data
        if (sections.length === data.queries.length) {
            sections.value = sections.value.sort(
                (a, b) => data.queries.findIndex(query => query.id === a.query?.id) - data.queries.findIndex(query => query.id === b.query?.id)
            );
        }

        if (newPullRequests.length && supportsCustomQueries) {
            this.updatePullRequestsWithStats(newPullRequests);
        }

        // Fetch more query results if not all were returned
        if (data.partialResults) {
            const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
            contributionService
                .getDataAsync<IPullRequestListData>(
                    prListDataProviderId,
                    {
                        queryIds: data.queries.filter(q => !data.queryResults[q.id]).map(q => q.id),
                        existingPullRequestResults: Object.keys(data.pullRequests)
                    },
                    true
                )
                .then(data => {
                    this.dataToSections(sections, data!, supportsCustomQueries);
                });
        } else {
            sections.ready.value = true;
        }
    }

    private addPullRequestsToSection(
        section: IInternalPullRequestSection,
        pullRequests: GitPullRequest[],
        hasMore: boolean | undefined,
        tabs: ObservableArray<IVssContributedTab> | undefined,
        selectedTabId: ObservableValue<string> | undefined
    ): IPullRequest[] {
        const reposRoutingService = this.pageContext.getService<IReposRoutingService>("IReposRoutingService");
        const { allPullRequests } = section;

        const convertedPrs = pullRequests.map(pr => convertPullRequest(pr, reposRoutingService, this.userId, this.personaProvider));
        allPullRequests.push(...convertedPrs);
        section.hasMoreResults.value = !!(hasMore && section.loadMore);

        if (section.pullRequests.length > 0) {
            // Remove the prior loading row
            section.pullRequests.pop();
        }

        if (section.hasMoreResults.value) {
            // More results are available. Add a show more/loading row.
            section.lastPullRequestId = convertedPrs[convertedPrs.length - 1].id;
            section.countText.value = `${allPullRequests.length}+`;
        } else {
            section.lastPullRequestId = 0;
            section.countText.value = allPullRequests.length > 0 ? allPullRequests.length.toString() : "";
        }

        // Set the tabs based on the current results
        if (tabs && selectedTabId) {
            // Make sure the selected tab id exists. Change to active otherwise.
            const activePrs = this.getPullRequestsForTab(section, "activeMine");
            const waitingPrs = this.getPullRequestsForTab(section, "waitingForAuthor");
            const reviewedPrs = this.getPullRequestsForTab(section, "reviewed");
            const declinedPrs = this.getPullRequestsForTab(section, "declined");

            const newTabs: IVssContributedTab[] = [];
            newTabs.push({
                id: "activeMine",
                badgeCount: activePrs.length > 0 ? activePrs.length : undefined,
                name: Resources.Active
            });
            if (waitingPrs.length > 0) {
                newTabs.push({
                    id: "waitingForAuthor",
                    badgeCount: waitingPrs.length > 0 ? waitingPrs.length : undefined,
                    name: Resources.WaitingForAuthor
                });
            }
            if (reviewedPrs.length > 0) {
                newTabs.push({
                    id: "reviewed",
                    badgeCount: reviewedPrs.length > 0 ? reviewedPrs.length : undefined,
                    name: Resources.Reviewed
                });
            }
            if (declinedPrs.length > 0) {
                newTabs.push({
                    id: "declined",
                    badgeCount: declinedPrs.length > 0 ? declinedPrs.length : undefined,
                    name: Resources.Declined
                });
            }

            tabs.value = newTabs;
            if (!newTabs.find(t => t.id === selectedTabId.value)) {
                selectedTabId.value = "activeMine";
            }
        }

        this.filterResultsToTab(section, selectedTabId?.value);

        return convertedPrs;
    }

    private filterResultsToTab(section: IInternalPullRequestSection, selectedTabId: string | undefined): void {
        const filteredPullRequests = this.getPullRequestsForTab(section, selectedTabId);
        if (section.hasMoreResults.value) {
            filteredPullRequests.push(new ObservableValue(undefined));
        }
        section.pullRequests.value = filteredPullRequests;
    }

    private getPullRequestsForTab(section: IInternalPullRequestSection, selectedTabId: string | undefined) {
        const groupByVote = section.query?.groupByVote;
        const pullRequests = section.allPullRequests;
        let filteredPullRequests: Array<IPullRequest | ObservableValue<IPullRequest | undefined>>;
        if (selectedTabId === "activeMine") {
            if (groupByVote) {
                filteredPullRequests = pullRequests.filter(pr => {
                    const myReviewer = this.getMyReview(pr);
                    return !myReviewer || (!myReviewer.hasDeclined && myReviewer?.vote === PullRequestVote.awaitingResponse);
                });
            } else {
                filteredPullRequests = pullRequests.filter(pr => {
                    const myReviewer = this.getMyReview(pr);
                    return !myReviewer || !myReviewer.hasDeclined;
                });
            }
        } else if (selectedTabId === "reviewed") {
            if (groupByVote) {
                filteredPullRequests = pullRequests.filter(pr => {
                    const myReviewer = this.getMyReview(pr);
                    return myReviewer && !myReviewer.hasDeclined && this.hasNonWaitingVote(myReviewer);
                });
            } else {
                filteredPullRequests = [];
            }
        } else if (selectedTabId === "declined") {
            filteredPullRequests = pullRequests.filter(pr => {
                const myReviewer = this.getMyReview(pr);
                return myReviewer && myReviewer.hasDeclined;
            });
        } else if (selectedTabId === "waitingForAuthor") {
            if (groupByVote) {
                filteredPullRequests = pullRequests.filter(pr => {
                    const myReviewer = this.getMyReview(pr);
                    return myReviewer && !myReviewer.hasDeclined && myReviewer.vote === PullRequestVote.notReady;
                });
            } else {
                filteredPullRequests = [];
            }
        } else {
            filteredPullRequests = [...pullRequests];
        }

        filteredPullRequests.sort(compareByFlagAndRequired);
        return filteredPullRequests;
    }

    private getMyReview(pullRequest: IPullRequest): IPullRequestReviewer | undefined {
        return pullRequest.reviewers.find(r => r.identity.id === this.userId);
    }

    private hasNonWaitingVote(reviewer: IPullRequestReviewer): boolean {
        return reviewer.vote !== PullRequestVote.notReady && reviewer.vote !== PullRequestVote.awaitingResponse;
    }

    private loadMoreQueryResults(
        section: IInternalPullRequestSection,
        tabs: ObservableArray<IVssContributedTab> | undefined,
        selectedTabId: ObservableValue<string> | undefined
    ): Promise<IPullRequest[]> {
        if (!section.query || !section.lastPullRequestId) {
            return Promise.resolve([]);
        }

        const query = section.query;
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        return contributionService
            .getDataAsync<IPullRequestListData>(
                prListDataProviderId,
                {
                    queryIds: [query.id],
                    lastPullRequestId: section.lastPullRequestId,
                    skip: section.allPullRequests.length
                },
                true
            )
            .then(data => {
                const results = data?.queryResults[query.id];
                if (results) {
                    const pullRequests = this.addPullRequestsToSection(
                        section,
                        results.ids.map(id => data!.pullRequests[id]),
                        results.hasMore,
                        tabs,
                        selectedTabId
                    );
                    this.updatePullRequestsWithStats(pullRequests);

                    return pullRequests;
                } else {
                    return [];
                }
            });
    }

    private getPullRequestStatUpdates(artifactStats: IPullRequestArtifactStats): string {
        if (!artifactStats.newThreadsCount) {
            return "";
        }

        const numberOfUpdates: { count: number; text: string }[] = [];

        const allChanges: number = artifactStats.newThreadsCount[CommentThreadType.all];
        const numPushes: number = artifactStats.newThreadsCount[CommentThreadType.iteration];
        const numComments: number = artifactStats.newThreadsCount[CommentThreadType.text];
        const numVotes: number = artifactStats.newThreadsCount[CommentThreadType.vote];

        if (numComments) {
            const template = numComments === 1 ? Resources.New_Comment_Singular : Resources.New_Comment_Plural;
            numberOfUpdates.push({ count: numComments, text: format(template, numComments) });
        }

        if (numPushes) {
            const template = numPushes === 1 ? Resources.New_Push_Singular : Resources.New_Push_Plural;
            numberOfUpdates.push({ count: numPushes, text: format(template, numPushes) });
        }

        if (numVotes) {
            const template = numVotes === 1 ? Resources.New_Vote_Singular : Resources.New_Vote_Plural;
            numberOfUpdates.push({ count: numVotes, text: format(template, numVotes) });
        }

        const numOtherChanges: number = allChanges - numPushes - numComments - numVotes;
        if (numOtherChanges) {
            let template = numOtherChanges === 1 ? Resources.OtherChange_Singular : Resources.OtherChange_Plural;
            if (!numberOfUpdates.length) {
                template = numOtherChanges === 1 ? Resources.Change_Singular : Resources.Change_Plural;
            }
            numberOfUpdates.push({ count: numOtherChanges, text: format(template, numOtherChanges) });
        }

        if (numberOfUpdates.length <= 2) {
            return numberOfUpdates.map(upd => upd.text).join(", ");
        }

        const overflowChanges = numberOfUpdates.slice(2 - 1);
        const overflowChangesCount = overflowChanges.reduce((sum, change) => sum + change.count, 0);

        const visibleChanges = numberOfUpdates.slice(0, 2 - 1).map(upd => upd.text);
        const template = overflowChangesCount === 1 ? Resources.OtherChange_Singular : Resources.OtherChange_Plural;
        visibleChanges.push(format(template, overflowChangesCount));

        return visibleChanges.join(", ");
    }

    private async updateReviewer(pullRequest: IPullRequest, patchReviewer: IdentityRefWithVote) {
        const { currentReviewer } = pullRequest;
        let updatedReviewer: IdentityRefWithVote;
        if (!currentReviewer) {
            updatedReviewer = await getGitClient(this.pageContext).createPullRequestReviewer(
                patchReviewer,
                pullRequest.repository.id,
                pullRequest.id,
                patchReviewer.id,
                pullRequest.repository.projectId
            );
        } else {
            updatedReviewer = await getGitClient(this.pageContext).updatePullRequestReviewer(
                patchReviewer,
                pullRequest.repository.id,
                pullRequest.id,
                patchReviewer.id,
                pullRequest.repository.projectId
            );
        }

        // Refresh updated pull request on sections so we don't need to fetch again.
        const freshPullRequest = { ...pullRequest, currentReviewer: convertReviewer(updatedReviewer), reviewers: [...pullRequest.reviewers] };
        const reviewerIndex = currentReviewer ? freshPullRequest.reviewers.indexOf(currentReviewer) : -1;
        if (reviewerIndex >= 0) {
            freshPullRequest.reviewers.splice(reviewerIndex, 1, freshPullRequest.currentReviewer);
        }

        for (const section of this.sections.value) {
            const prIndex = section.pullRequests.value.findIndex((pr: IPullRequest) => pr.id === pullRequest.id);
            if (prIndex >= 0) {
                section.pullRequests.splice(prIndex, 1, freshPullRequest);
            }

            // Also update allPullRequests so when tab switches, the pull request is displayed correctly.
            const allPrIndex = section.allPullRequests.findIndex((pr: IPullRequest) => pr.id === pullRequest.id);
            if (allPrIndex >= 0) {
                section.allPullRequests.splice(allPrIndex, 1, freshPullRequest);
            }
        }
    }
}

function compareByFlagAndRequired(
    a: IPullRequest | ObservableValue<IPullRequest | undefined>,
    b: IPullRequest | ObservableValue<IPullRequest | undefined>
): number {
    const prA = ObservableLike.getValue(a);
    const prB = ObservableLike.getValue(b);
    const isFlaggedA = prA && prA.currentReviewer && prA.currentReviewer.isFlagged;
    const isFlaggedB = prB && prB.currentReviewer && prB.currentReviewer.isFlagged;

    if (isFlaggedA) {
        return isFlaggedB ? 0 : -1;
    } else if (isFlaggedB) {
        return 1;
    } else {
        const isRequiredA = prA && prA.currentReviewer && prA.currentReviewer.required;
        const isRequiredB = prB && prB.currentReviewer && prB.currentReviewer.required;
        if (isRequiredA) {
            return isRequiredB ? 0 : -1;
        } else {
            return isRequiredB ? 1 : 0;
        }
    }
}

const enum CommentThreadType {
    all = 0,
    system = 1,
    text = 2,
    reviewLevel = 3,
    code = 4,
    iteration = 5,
    vote = 6
}

export function getPullRequestDataProviderService(pageContext: IVssPageContext): IPullRequestDataProviderService {
    return pageContext.getService<IPullRequestDataProviderService>("IPullRequestDataProviderService");
}

Services.add("IPullRequestDataProviderService", { serviceFactory: PullRequestDataProviderService });
